/**
 * Outfits and mascot › Facing (GET /v1/admin/desktop/mascot/facing/summary, POST …/facing/backfill; owner, 2026-10-15:
 * "many of the outfits uploaded were not mirrored so that gyro looks left by default"): how many outfits face which
 * way, the review list of the unclear ones, and Run facing backfill, which calls the service batch after batch until
 * it says it is done (waiting when it says the rate or the day's budget stopped it) and shows the running counts. A
 * scope choice (lib/outfitFacing.ts BACKFILL_SCOPES) says which outfits a run judges: the ones not judged yet (the
 * default), the unclear ones, the ones the model judged front, or everything the model judged; a service before scopes
 * ignores it and judges the ones not judged yet. An outfit's own Flip, Undo flip and Mark buttons are in the gallery's
 * inspector (DesktopMascotCard.tsx). Hidden on a service without the route.
 */
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Compass, Loader2, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  BACKFILL_SCOPES,
  EMPTY_COUNTS,
  FACING_BACKFILL_HINT,
  FACING_CONVENTION_TEXT,
  addCounts,
  backfillButtonText,
  backfillDoneText,
  backfillProgressText,
  backfillScopeCount,
  facingNoteText,
  facingSummaryText,
  type BackfillCounts,
  type BackfillScope,
  type FacingSummary,
} from '../lib/outfitFacing';
import { Button, Callout, Field, Icon, Panel, Select, Skeleton } from '../ui';
import { SaveNote } from './SaveNote';

export interface OutfitFacingSectionProps {
  /** Writes are off: the admin is an auditor, or the card is busy. */
  locked: boolean;
  /** The backfill changed outfits (mirrored some): the card reads the catalog again. */
  onChanged: () => Promise<unknown> | void;
  /** Select an outfit in the gallery (the review list's names). */
  onSelect?: (id: string) => void;
  /** Test seam: how long to wait when the service says to come back later (default: what it says, capped at 30 s). */
  waitMs?: (retryAfterSec: number) => number;
}

type Read =
  | { status: 'loading' }
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; summary: FacingSummary };

interface Run {
  counts: BackfillCounts;
  total: number;
  remaining: number;
  /** Waiting for the service's retryAfterSec before the next batch. */
  waiting: string | null;
}

export const FACING_UNREADABLE_TEXT = 'The facings could not be read just now.';
export const FACING_BACKFILL_DONE_TEXT = backfillDoneText('unjudged');

export function OutfitFacingSection({ locked, onChanged, onSelect, waitMs }: OutfitFacingSectionProps) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [run, setRun] = useState<Run | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [scope, setScope] = useState<BackfillScope>('unjudged');
  const stop = useRef(false);

  const load = useCallback(async (): Promise<FacingSummary | null> => {
    try {
      const summary = await api.desktopMascotFacingSummary();
      setRead(summary === null ? { status: 'absent' } : { status: 'loaded', summary });
      return summary;
    } catch (error) {
      setRead({ status: 'failed', text: errorText(error, FACING_UNREADABLE_TEXT) });
      return null;
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      stop.current = true;
    };
  }, [load]);

  const backfill = async () => {
    stop.current = false;
    setNote(null);
    let counts = EMPTY_COUNTS;
    let cursor: string | null = null;
    let changed = false;
    setRun({ counts, total: 0, remaining: 0, waiting: null });
    try {
      for (;;) {
        const answer = await api.runDesktopMascotFacingBackfill({ cursor, scope });
        counts = addCounts(counts, answer.counts);
        changed ||= answer.counts.mirrored > 0;
        cursor = answer.cursor;
        if (answer.done) break;
        if (answer.stopped) {
          const wait = waitMs ? waitMs(answer.stopped.retryAfterSec) : Math.min(30, answer.stopped.retryAfterSec) * 1000;
          const why = answer.stopped.reason === 'budget' ? 'the day’s model budget is used up' : answer.stopped.reason === 'rate' ? 'the model rate' : answer.stopped.reason === 'audit' ? 'the audit ledger' : 'storage';
          if (answer.stopped.reason === 'budget') {
            setRun({ counts, total: answer.total, remaining: answer.remaining, waiting: null });
            setNote({ ok: false, text: `Stopped: ${why}. ${answer.remaining.toLocaleString('en-US')} outfits are still to be judged; run it again tomorrow.` });
            return;
          }
          setRun({ counts, total: answer.total, remaining: answer.remaining, waiting: `Waiting ${Math.ceil(wait / 1000)} s for ${why}…` });
          await new Promise((resolve) => setTimeout(resolve, wait));
          if (stop.current) return;
        } else {
          setRun({ counts, total: answer.total, remaining: answer.remaining, waiting: null });
        }
      }
      setRun({ counts, total: counts.scanned, remaining: 0, waiting: null });
      setNote({ ok: true, text: `${backfillDoneText(scope)} ${backfillProgressText(counts, counts.scanned, 0)}.` });
    } catch (error) {
      setNote({ ok: false, text: errorText(error, 'The backfill stopped.') });
    } finally {
      if (!stop.current) {
        setRun((current) => (current ? { ...current, waiting: null } : current));
        await load();
        if (changed) await onChanged();
        setRun(null);
      }
    }
  };

  if (read.status === 'absent') return null;
  const summary = read.status === 'loaded' ? read.summary : null;
  const running = run !== null;
  const scopeCount = summary ? backfillScopeCount(summary, scope).count : 0;
  return (
    <Panel
      heading="h3"
      titleId="mascot-facing-title"
      title={<span className="ui-title-icon"><Icon icon={Compass} size="md" />Facing</span>}
      description="Which way each outfit faces: every outfit faces left"
      actions={(
        <Button size="sm" variant="ghost" icon={RefreshCw} disabled={running} onClick={() => void load()} title="Read the counts again">
          Refresh
        </Button>
      )}
      footer={<span>{FACING_CONVENTION_TEXT} {FACING_BACKFILL_HINT}{summary ? ` The model: ${summary.model}; today’s spend $${summary.spend.usd.toFixed(2)} of $${summary.spend.capUsd.toFixed(2)}.` : ''}</span>}
    >
      <div className="ui-form">
        {read.status === 'loading' && <Skeleton size="md" width="60%" />}
        {read.status === 'failed' && <Callout tone="warn" role="alert">{read.text}</Callout>}
        {summary && (
          <>
            <p className="ui-cell-stack" data-testid="facing-summary">{facingSummaryText(summary)}</p>
            <Field label="Which outfits to judge" help="An outfit an admin flipped, undid or marked is never judged again.">
              <Select
                size="sm"
                value={scope}
                disabled={running}
                aria-label="Which outfits to judge"
                data-testid="facing-scope"
                onChange={(event) => setScope(event.target.value as BackfillScope)}
              >
                {BACKFILL_SCOPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            </Field>
            <div className="ui-actions">
              <Button
                size="sm"
                icon={running ? Loader2 : Compass}
                busy={running}
                disabled={locked || running || scopeCount === 0}
                onClick={() => void backfill()}
                title={scopeCount === 0 ? 'There is no outfit to judge in this scope.' : FACING_BACKFILL_HINT}
              >
                {backfillButtonText(summary, scope)}
              </Button>
              {run && (
                <span className="ui-cell-sub" role="status" data-testid="facing-progress">
                  {backfillProgressText(run.counts, Math.max(run.total, run.counts.scanned + run.remaining), run.remaining)}
                  {run.waiting && <> · {run.waiting}</>}
                </span>
              )}
            </div>
            <SaveNote note={note} />
            {(summary.unclear.length > 0 || summary.notMirrored.length > 0) && (
              <details className="ui-details" open={summary.unclear.length + summary.notMirrored.length <= 25}>
                <summary>To review: {summary.unclear.length.toLocaleString('en-US')} unclear, {summary.notMirrored.length.toLocaleString('en-US')} facing right and not mirrored</summary>
                <ul className="ui-list" data-testid="facing-review">
                  {[...summary.unclear.map((row) => ({ ...row, kind: 'unclear' as const })), ...summary.notMirrored.map((row) => ({ ...row, kind: 'right' as const }))].map((row) => (
                    <li key={row.id}>
                      {onSelect ? <Button variant="ghost" size="sm" onClick={() => onSelect(row.id)} title="Select it in the gallery">{row.name}</Button> : row.name}
                      {' '}· {row.kind === 'unclear' ? 'unclear' : 'faces right'}: {facingNoteText(row.note)}. Select it in the gallery to flip it or mark which way it faces.
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}
